import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useEffect } from "react";
import { useQuery } from "react-query";

import type { ChatRoom } from "@/react/types/Chat/ChatRoom";

import { selectActions, useChatRoomStoreChatRoom } from "../chatsV2/store";
import { useChatStore } from "../chatsV2/store/useChatStore";

interface ChatRoomShowResponse {
  chat_room: ChatRoom;
}

export const embeddableChatApiQueryKey = (chatRoomUuid?: string | null) =>
  `embeddable-chat-room-${chatRoomUuid}`;

export const useEmbeddableChatApi = (chatRoomUuid?: string | null) => {
  const { updateChatRoom } = useChatStore(selectActions);
  const chatRoomStoreState = useChatRoomStoreChatRoom();

  const {
    isInitialLoading,
    refetch: refetchChatRoom,
    data: { chat_room: chatRoom } = {},
  } = useQuery<ChatRoomShowResponse>({
    queryKey: [embeddableChatApiQueryKey(chatRoomUuid)],
    queryFn: () =>
      reactQueryGet<ChatRoomShowResponse>(
        internalApi.chatRooms.show({ uuid: chatRoomUuid }),
      ),
    enabled: Boolean(chatRoomUuid) && chatRoomUuid !== "new",
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (chatRoom) {
      updateChatRoom(chatRoom);
    }
  }, [chatRoom, updateChatRoom]);

  const onEventReceive = (eventData: any) => {
    if (eventData.event === "chatRoomUpdated") {
      const { chat_room: chatRoomData } = eventData;
      updateChatRoom({
        ...chatRoomData,
        pinned_message: chatRoomData.pinned_message,
      });
    }
    if (
      eventData.event === "updatedMessage" &&
      chatRoomStoreState?.pinned_message?.id === eventData.json_message.id &&
      chatRoom?.uuid
    ) {
      updateChatRoom({
        uuid: chatRoom.uuid,
        pinned_message: eventData.json_message,
      });
    }
    if (
      eventData.event === "deletedMessage" &&
      chatRoomStoreState?.pinned_message?.id === eventData.id &&
      chatRoom?.uuid
    ) {
      updateChatRoom({
        uuid: chatRoom.uuid,
        pinned_message: null,
      });
    }
  };

  return {
    isLoading: isInitialLoading,
    chatRoom,
    refetchChatRoom: chatRoomUuid ? refetchChatRoom : undefined,
    onEventReceive,
  };
};

export type EmbeddableChatRefetchChatRoom = NonNullable<
  ReturnType<typeof useEmbeddableChatApi>["refetchChatRoom"]
>;
